// src/views/words-lists/ClassicListView.tsx
/* eslint-disable max-len */
import React, {
  FC, useCallback, useState, useMemo, useEffect,
  useRef,
} from 'react';
import {
  List, Skeleton, Tooltip, Divider,
} from 'antd';
import { FolderOutlined } from '@ant-design/icons';
import { TbStack2 } from 'react-icons/tb';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './ClassicListsView.module.less';
import { getIsLoadingWordsListList, getListOfWordsList } from './slice/selectors';
import { fetchWordsListPage } from './slice/actions';
import DeleteWordsListModal from './modals/DeleteWordsListModal';
import ShareWordsListModal from './modals/ShareWordsListModal';
import { getWordsListLink } from '../../constants/Path';
import { ReactComponent as Edit } from '../../assets/icons/teacher/edit.svg';
import { ReactComponent as Share } from '../../assets/icons/teacher/share.svg';
import { ReactComponent as Thrash } from '../../assets/icons/teacher/trash.svg';
import { setActiveList } from '../auth/slice/actions';
import { getActiveList } from '../auth/slice/selectors';
import { getFolderTreeWithLists, getIsLoadingTree } from '../folders/slice/selectors';
import { fetchFolderTreeWithLists, invalidateCache } from '../folders/slice/actions';
import { TreeNodeType } from '../../services/ApiService';
import { pluralForm } from '../../utils/plural';

const ClassicListView: FC = () => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const dispatch = useDispatch();
  const loading = useSelector(getIsLoadingWordsListList);
  const activeList = useSelector(getActiveList);

  const folderTreeData = useSelector(getFolderTreeWithLists);
  const isLoadingTree = useSelector(getIsLoadingTree);
  const wordsListsData = useSelector(getListOfWordsList);
  const hasInitiallyFetchedRef = useRef(false);

  const [deletedWordsList, setDeletedWordsList] = useState<{ id: number, name: string } | null>(null);
  const [sharedWordsList, setSharedWordsList] = useState<{ id: number, name: string, sharedStudentIds: number[] } | null>(null);
  const [clickedItem, setClickedItem] = useState<{ id: number, wordsLength: number } | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ✅ KLUCZ 1: Wyciągaj listy z drzewa i łącz z pełnymi danymi
  const extractListsFromTree = useCallback((nodes: TreeNodeType[]): any[] => nodes.reduce((acc: any[], node) => {
    if (node.type === 'list') {
      const listId = parseInt(node.id.replace('list-', ''), 10);
      const fullData = wordsListsData.find((item: any) => item.id === listId);

      acc.push({
        id: listId,
        name: node.name,
        sharedStudentIds: fullData?.sharedStudentIds || node.sharedStudentIds || [],
        words: fullData?.words || node.words || [],
        updatedAt: node.updatedAt || node.data?.updatedAt,
        createdAt: node.data?.createdAt,
        wordsCount: fullData?.words?.length || node.data?.wordsCount || 0,
        shared: node.data?.shared || false,
      });
    }
    if (node.children && node.children.length > 0) {
      acc.push(...extractListsFromTree(node.children));
    }

    return acc;
  }, []), [wordsListsData]);

  const lists = useMemo(() => extractListsFromTree(folderTreeData), [folderTreeData, extractListsFromTree]);

  useEffect(() => {
    if (!hasInitiallyFetchedRef.current && folderTreeData.length === 0) {
      console.log('📋 ClassicListView: Pierwszy fetch danych...');
      hasInitiallyFetchedRef.current = true;
      dispatch(fetchFolderTreeWithLists(true));
    }
  }, [folderTreeData.length, dispatch]);

  const sortedListsByDate = useMemo(() => [...lists].sort((a, b) => {
    const dateA = new Date(a.updatedAt).getTime();
    const dateB = new Date(b.updatedAt).getTime();

    return dateB - dateA;
  }), [lists]);

  const onItemClick = useCallback((item) => {
    push(getWordsListLink(item.id));
  }, [push]);

  const hideShareModal = useCallback(() => {
    setSharedWordsList(null);
  }, []);

  const showShareModal = useCallback((wordsList: { id: number, name: string, sharedStudentIds: number[] }) => {
    setSharedWordsList(wordsList);
  }, []);

  // ✅ KLUCZ 4: Odświeżaj po usunięciu
  const hideDeleteModal = useCallback(() => {
    setDeletedWordsList(null);

    // ✅ Po zamknięciu modala usuwania, odśwież dane
    console.log('📋 ClassicListView: Lista usunięta, odświeżam dane...');
    dispatch(invalidateCache());
    dispatch(fetchFolderTreeWithLists(true));
    dispatch(fetchWordsListPage(1));
  }, [dispatch]);

  const showDeleteModal = useCallback(
    (wordsList: { id: number, name: string }) => setDeletedWordsList(wordsList),
    [],
  );

  const toggleActiveWordsList = useCallback((item) => {
    if (!activeList && item.wordsCount >= 2) {
      dispatch(setActiveList(item));
    } else if (activeList && activeList.id === item.id) {
      dispatch(setActiveList(null));
    } else if (activeList && item.wordsCount >= 2) {
      dispatch(setActiveList(item));
    }

    if (item.wordsCount < 2) {
      setClickedItem({ id: item.id, wordsLength: item.wordsCount });
      setTimeout(() => setClickedItem(null), 2500);
    } else {
      setClickedItem(null);
    }
  }, [activeList, dispatch]);

  const loadMoreData = useCallback(async () => {
    if (loading || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    try {
      dispatch(fetchWordsListPage(1));
    } catch (error) {
      console.error('Error loading more lists:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, loading, isLoadingMore]);

  return (
    <>
      {deletedWordsList && (
        <DeleteWordsListModal
          wordsList={deletedWordsList}
          hideModal={hideDeleteModal}
        />
      )}
      {sharedWordsList && (
        <ShareWordsListModal
          wordsList={sharedWordsList}
          hideModal={hideShareModal}
        />
      )}

      {/* Licznik list w prawym dolnym rogu panelu — poza przewijanym obszarem, więc stoi w miejscu.
          Bez list (także zanim drzewo się wczyta) go nie ma — nie miga „0” */}
      <div className={styles.listBox}>
        {sortedListsByDate.length > 0 && (
          <Tooltip title={t('words-list:lists.count')}>
            <span className={styles.listsCount}>
              <TbStack2 className={styles.listsCountIcon} />
              <b>{sortedListsByDate.length}</b>
            </span>
          </Tooltip>
        )}
        <div
          id="scrollableDiv"
          className={styles.listWrapper}
        >
          {/* Pusty stan jak w zakładce „Katalog” (TreeContainer); w trakcie wczytywania drzewa nic — nie miga */}
          {sortedListsByDate.length === 0 && !isLoadingTree && (
          <div className={styles.emptyState}>
            <FolderOutlined style={{ fontSize: 48, opacity: 0.3 }} />
            <p>{t('common:emptyHere')}</p>
          </div>
          )}
          {sortedListsByDate.length > 0 && (
          <InfiniteScroll
            dataLength={sortedListsByDate.length}
            next={loadMoreData}
            hasMore={false}
            loader={<Skeleton avatar paragraph={{ rows: 1 }} active />}
            endMessage={<Divider />}
            scrollableTarget="scrollableDiv"
          >
            <List
              dataSource={sortedListsByDate}
              renderItem={(item: { id: number, name: string, sharedStudentIds: number[], words: any[], wordsCount: number }) => (
                <List.Item
                  key={item.id}
                  className={`${styles.listPoint} ${item.id === activeList?.id ? styles.activeRow : ''}`}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleActiveWordsList(item)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleActiveWordsList(item);
                      }
                    }}
                    className={styles.listsNames}
                    aria-label={`${item.name}, ${t(`words-list:itemsCount.${pluralForm(item.words.length)}`, { n: item.words.length })}`}
                    aria-pressed={activeList?.id === item.id}
                  >
                    <div className={`${styles.listItems} ${clickedItem && clickedItem.id === item.id && clickedItem.wordsLength < 2 ? `${styles.clickItem} ${styles['clickedItem-animation']}` : ''}`}>
                      {item.name}
                    </div>
                  </div>

                  {/* Liczba elementów przed listwą ikon — to nie przycisk */}
                  <div className={styles.actionsContainer}>
                    <Tooltip title={t('words-list:lists.itemsTooltip')} aria-labelledby={t('words-list:lists.itemsTooltip')}>
                      <div
                        className={`${styles.wordsCount} ${clickedItem && clickedItem.id === item.id && clickedItem.wordsLength < 2 ? `${styles.clickedItem} ${styles['listTooShort-animation']}` : ''} ${styles.itemsNumber}`}
                        style={{ cursor: 'default' }}
                        id={styles.itemsNumber}
                        aria-label={t(`words-list:itemsCount.${pluralForm(item.wordsCount)}`, { n: item.wordsCount })}
                      >
                        <span>{item.wordsCount}</span>
                      </div>
                    </Tooltip>

                    <div className={styles.actions}>
                      {item && (
                      <button
                        className={styles.switchActive}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onItemClick(item);
                        }}
                        aria-label={t('words-list:lists.edit', { list: item.name })}
                      >
                        <Tooltip title={t('words-list:lists.editTooltip')} aria-labelledby={t('words-list:lists.editTooltip')}>
                          <span><Edit className={styles.icon} aria-hidden="true" /></span>
                        </Tooltip>
                      </button>
                      )}

                      <button
                        className={styles.switchActive}
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          showShareModal(item);
                        }}
                        aria-label={t('words-list:lists.share', { list: item.name })}
                      >
                        <Tooltip title={t('words-list:lists.shareTooltip')} aria-labelledby={t('words-list:lists.shareTooltip')}>
                          <span><Share className={styles.icon} aria-hidden="true" /></span>
                        </Tooltip>
                      </button>

                      <button
                        className={`${styles.switchActive} ${styles.deleteAction}`}
                        type="button"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          showDeleteModal(item);
                        }}
                        aria-label={t('words-list:lists.delete', { list: item.name })}
                      >
                        <Tooltip title={t('words-list:lists.deleteTooltip')} aria-labelledby={t('words-list:lists.deleteTooltip')}>
                          <span><Thrash className={styles.icon} aria-hidden="true" /></span>
                        </Tooltip>
                      </button>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </InfiniteScroll>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(ClassicListView);
